import { assertNever, definedMap } from "@glideapps/ts-necessities";
import chroma from "chroma-js";
import classNames from "classnames";
import { massageImageUrl } from "common/components/portable-renderers";
import { Appearance } from "common/generator/components/button-appearance";
import type { WireListCardCollection } from "common/generator/components/fluent-components";
import { isDefined } from "common/Support";
import type { UISizeSimple, Unbound } from "common/wire/types";
import { CardStyle, isBound, UIAspect, UIBackgroundStyle, UISize, UnboundVal } from "common/wire/types";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { SectionStyleProvider, useSectionStyle } from "webapp/ui/views/wire-container/wire-container";
import { WireListContainer } from "webapp/ui/views/wire-list-container/wire-list-container";
import { WireMenuButton } from "webapp/ui/views/wire-menu-button/wire-menu-button";
import { useResponsiveSizeClass } from "webapp/wire-renderers/responsive-provider";
import type { ExtractedActions } from "webapp/wire-renderers/wire-lib";
import { breakoutActions, extractActions, makeActionSpreadProps } from "webapp/wire-renderers/wire-lib";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";
import Img from "../img/img";

export interface ItemDescription {
    readonly title?: string | Unbound;
    readonly subtitle?: string | Unbound;
    readonly emphasis?: string | Unbound;
    readonly body?: string | Unbound;
    readonly image?: string | Unbound;
    readonly heroImage?: string | Unbound;
    readonly onCardClick?: () => void | Unbound;
    readonly menuItems?: ExtractedActions;
}

function rangeForPage(page: number, numPages: number, size: number): (number | null)[] {
    let start = page - size;
    let end = page + size;

    const upper = size * 2;

    if (start < 0) {
        end += -start;
        start = 0;
    }
    if (end >= numPages) {
        const d = end - (numPages - 1);
        start = Math.max(0, start - d);
        end = numPages - 1;
    }

    const result: (number | null)[] = [];
    for (let x = start; x <= end; x++) {
        result.push(x);
    }

    if (result[0] !== 0) {
        result[0] = 0;
        result[1] = null;
    }

    if (numPages > upper && result[upper] !== numPages - 1) {
        result[upper] = numPages - 1;
        result[upper - 1] = null;
    }

    return result;
}

interface Props<T> {
    readonly cardStyle: CardStyle;
    readonly pageItems: readonly T[];
    readonly page: number;
    readonly size: UISizeSimple;
    readonly aspect: UIAspect;
    readonly numPages: number;
    readonly describeItem: (item: T) => ItemDescription;
    readonly onPageChange?: (page: number) => void;
}

interface CardProps {
    desc: ItemDescription;
}

const CardContainer: React.FC<{ size?: UISize; aspect: UIAspect }> = p => {
    const { children, size = UISize.Original, aspect } = p;
    // There is a certain amount of tuning in the minmax's below to make sure that when the padding
    // adjusts on page size changes that it doesn't cause flickering. If you want to tune these values
    // make sure to test that when resizing very very slowly there isn't a pixel where it bounces through
    // a different number of cols.
    return (
        <div
            tw="grid w-full"
            className={classNames(size === UISize.Small ? "small" : size === UISize.Large ? "large" : "medium", aspect)}
            css={css`
                &.small {
                    ${tw`text-lg gap-4 grid-template-columns[repeat(auto-fill, minmax(14.125rem, 1fr))]
                      gp-sm:gap-5
                      gp-xl:gap-6`}
                }
                &.medium {
                    ${tw`text-xl gap-4 grid-template-columns[repeat(auto-fill, minmax(14.125rem, 1fr))]
                      gp-sm:(gap-5 grid-template-columns[repeat(auto-fill, minmax(18.125rem, 1fr))])
                      gp-lg:gap-6`}
                }
                &.large {
                    ${tw`text-xl gap-4 grid-template-columns[repeat(auto-fill, minmax(14.125rem, 1fr))]
                      gp-sm:(gap-5 grid-template-columns[repeat(auto-fill, minmax(20.125rem, 1fr))])
                      gp-md:(grid-template-columns[repeat(auto-fill, minmax(24.125rem, 1fr))])
                      gp-lg:(grid-template-columns[repeat(auto-fill, minmax(25.125rem, 1fr))] gap-6)`}
                }

                --card-aspect: 56.25%;
                &.aspect-auto,
                &.aspect-sixteen-by-nine {
                    --card-aspect: 56.25%;
                }

                &.aspect-square {
                    --card-aspect: 100%;
                }

                &.aspect-four-by-three {
                    --card-aspect: 75%;
                }

                &.aspect-three-by-one {
                    --card-aspect: 33.33%;
                }

                &.aspect-three-by-two {
                    --card-aspect: 66.66%;
                }
            `}>
            {children}
        </div>
    );
};

const Card: React.VFC<CardProps & { size: UISize }> = p => {
    const { desc } = p;
    const [primary, secondary, ...menuActions] = breakoutActions(desc.menuItems, 2);
    return (
        <SectionStyleProvider value={UIBackgroundStyle.Highlight}>
            <div
                data-testid="cc-card"
                onClick={e => {
                    desc.onCardClick?.();
                    e.stopPropagation();
                }}
                css={[desc.onCardClick && tw`hover:shadow-xl-dark`]}
                tw="relative bg-n0 text-text-dark flex flex-col shadow-md w-full rounded-lg overflow-hidden
                    transition-shadow cursor-pointer">
                {desc.image !== UnboundVal && (
                    <div tw="relative w-full padding-top[var(--card-aspect)]">
                        <Img
                            src={massageImageUrl(
                                desc.image ?? undefined,
                                {
                                    thumbnail: false,
                                },
                                undefined
                            )}
                            tw="absolute inset-0 h-full w-full object-cover bg-n50"
                        />
                    </div>
                )}
                {desc.heroImage && (
                    <Img
                        src={massageImageUrl(desc.heroImage, { thumbnail: false, width: 96, height: 96 }, undefined)}
                        tw="w-24 h-24 self-center -mt-12 flex-shrink-0 rounded-full object-cover border-2 z-10
                            border-solid border-n0"
                    />
                )}
                <div tw="flex-grow flex flex-col p-4">
                    {desc.emphasis && (
                        <div tw="text-xs line-height[20px] uppercase text-accent font-bold">{desc.emphasis}</div>
                    )}
                    {desc.title && <div tw="font-extrabold leading-tight whitespace-pre-wrap">{desc.title}</div>}
                    {desc.subtitle && <div tw="text-sm text-text-pale whitespace-pre-wrap">{desc.subtitle}</div>}
                    <div tw="flex-grow" />
                    <div tw="flex all-child:(flex-1 mt-4)">
                        {primary !== undefined && (
                            <WireButton
                                data-testid="card-primary-button"
                                appearance={Appearance.Filled}
                                {...makeActionSpreadProps(primary)}
                            />
                        )}
                        {secondary !== undefined && (
                            <WireButton
                                tw="not-first:ml-2"
                                appearance={Appearance.Bordered}
                                {...makeActionSpreadProps(secondary)}
                            />
                        )}
                        {menuActions.length > 0 && (
                            <WireMenuButton
                                tw="not-first:ml-2"
                                appearance={Appearance.Bordered}
                                menuItems={menuActions}
                            />
                        )}
                    </div>
                </div>
            </div>
        </SectionStyleProvider>
    );
};

const CoverContainer: React.FC<{ size?: UISize }> = p => {
    const { children, size = UISize.Original } = p;
    return (
        <div
            tw="grid w-full"
            className={size === UISize.Small ? "small" : size === UISize.Large ? "large" : "medium"}
            css={css`
                &.small {
                    ${tw`gap-4 grid-template-columns[repeat(auto-fill, minmax(7.5rem, 1fr))]
                      gp-sm:gap-5
                      gp-xl:gap-6`}
                }
                &.medium {
                    ${tw`gap-4 grid-cols-2
                      gp-sm:gap-5
                      gp-lg:gap-6
                      gp-sm:grid-template-columns[repeat(auto-fill, minmax(10rem, 1fr))]`}
                }
                &.large {
                    ${tw`gap-4 grid-template-columns[repeat(auto-fill, minmax(12rem, 1fr))]
                      gp-sm:gap-5
                      gp-lg:gap-6`}
                }
            `}>
            {children}
        </div>
    );
};

const Cover: React.VFC<CardProps & { size: UISize }> = p => {
    const { desc } = p;
    const menuActions = desc.menuItems?.filter(isDefined) ?? [];

    return (
        <div
            onClick={e => {
                desc.onCardClick?.();
                e.stopPropagation();
            }}
            css={`
                @media (hover: hover) {
                    .menu {
                        opacity: 0;
                    }
                    &:hover .menu {
                        opacity: unset;
                    }
                }
            `}
            tw="text-text-dark cursor-pointer transition-opacity">
            {desc.image !== UnboundVal && (
                <div tw="w-full aspect-w-1 aspect-h-1">
                    <Img
                        css={[desc.onCardClick && tw`hover:shadow-xl-dark`, UISize.Large && tw`rounded-lg!`]}
                        src={massageImageUrl(
                            desc.image ?? undefined,
                            {
                                width: 384,
                                height: 384,
                                thumbnail: false,
                            },
                            undefined
                        )}
                        tw="absolute w-full h-full object-center object-cover rounded-md shadow-lg bg-n50
                            transition-shadow"
                    />
                    {menuActions.length > 0 && (
                        <WireMenuButton
                            className={classNames("menu")}
                            tw="absolute top-2 right-2 bg-white bg-opacity-20 hover:(bg-opacity-30) text-white
                                text-opacity-100 cursor-pointer rounded-full w-6 h-6 block max-w-none! p-0!"
                            css={css`
                                left: unset;
                            `}
                            appearance={Appearance.FullBleed}
                            menuItems={menuActions}
                        />
                    )}
                </div>
            )}
            <div tw="mt-2">
                {desc.emphasis && (
                    <div
                        css={css`
                            .small & {
                                font-size: 11px;
                            }
                        `}
                        tw="text-xs uppercase text-text-contextual-accent font-bold break-words">
                        {desc.emphasis}
                    </div>
                )}
                {desc.title && (
                    <div
                        css={css`
                            .small & {
                                ${tw`text-sm font-bold`}
                            }
                            .medium & {
                                ${tw`text-base font-bold`}
                            }
                            .large & {
                                ${tw`text-lg font-extrabold`}
                            }
                        `}
                        tw="text-text-contextual-dark whitespace-pre-wrap break-words">
                        {desc.title}
                    </div>
                )}
                {desc.subtitle && (
                    <div
                        css={css`
                            .small & {
                                ${tw`text-xs`}
                            }
                            .medium & .large & {
                                ${tw`text-sm`}
                            }
                        `}
                        tw="text-text-contextual-pale whitespace-pre-wrap break-words">
                        {desc.subtitle}
                    </div>
                )}
            </div>
        </div>
    );
};

const List: React.VFC<CardProps> = p => {
    const { desc } = p;
    const [primary, secondary, ...menuActions] = breakoutActions(desc.menuItems, 2);
    return (
        <SectionStyleProvider value={UIBackgroundStyle.Highlight}>
            <div
                onClick={e => {
                    desc.onCardClick?.();
                    e.stopPropagation();
                }}
                css={[desc.onCardClick && tw`hover:shadow-xl-dark`]}
                tw="relative bg-n0 p-4 text-text-dark flex flex-wrap shadow-md w-full rounded-lg overflow-hidden
                    transition-shadow
                    gp-sm:flex-nowrap
                    gp-md:p-6">
                <div tw="flex flex-1 items-center">
                    {isBound(desc.image) && (
                        <Img
                            src={massageImageUrl(desc.image, { thumbnail: false, width: 72, height: 72 }, undefined)}
                            tw="width[72px] height[72px] mr-4 flex-shrink-0 self-start object-cover bg-n50 rounded-lg
                                gp-md:mr-6"
                        />
                    )}
                    <div
                        tw="flex-grow flex flex-col
                            gp-md:mr-4">
                        {desc.emphasis && (
                            <div tw="font-size[12px] line-height[20px] uppercase text-accent font-bold">
                                {desc.emphasis}
                            </div>
                        )}
                        {desc.title && <div tw="text-lg font-extrabold whitespace-pre-wrap">{desc.title}</div>}
                        {desc.subtitle && <div tw="text-sm text-text-pale whitespace-pre-wrap">{desc.subtitle}</div>}
                    </div>
                </div>
                <div
                    tw="flex flex-basis[100%] self-center
                        gp-sm:(flex-initial mt-0)">
                    {primary !== undefined && (
                        <WireButton
                            data-testid="card-primary-button"
                            tw="flex-grow mt-4
                                gp-sm:mt-0"
                            appearance={Appearance.Filled}
                            {...makeActionSpreadProps(primary)}
                        />
                    )}
                    {secondary !== undefined && (
                        <WireButton
                            tw="flex-grow not-first:ml-2 mt-4
                                gp-sm:mt-0"
                            appearance={Appearance.Bordered}
                            {...makeActionSpreadProps(secondary)}
                        />
                    )}
                    {menuActions.length > 0 && (
                        <WireMenuButton
                            tw="not-first:ml-2 mt-4
                                gp-sm:mt-0"
                            appearance={Appearance.Bordered}
                            menuItems={menuActions}
                        />
                    )}
                </div>
            </div>
        </SectionStyleProvider>
    );
};

const Table: React.VFC<CardProps> = p => {
    const { desc } = p;
    const [primary, secondary, ...menuActions] = breakoutActions(desc.menuItems, 2);
    return (
        <SectionStyleProvider value={UIBackgroundStyle.Highlight}>
            <div
                className="group"
                onClick={e => {
                    desc.onCardClick?.();
                    e.stopPropagation();
                }}
                css={[desc.onCardClick && tw`cursor-pointer hover:bg-bg-middle`]}
                tw="relative border-solid border-border-base bg-n0 height[52px] px-6 text-text-dark items-center flex
                    w-full overflow-hidden transition-colors --button-opacity[0] hover:(--button-opacity[1])
                    focus-within:(--button-opacity[1]) not-last:(border-b)">
                {isBound(desc.image) && (
                    <Img
                        src={massageImageUrl(desc.image, { thumbnail: true, width: 32, height: 32 }, undefined)}
                        tw="w-8 h-8 mr-4 flex-shrink-0 object-cover bg-n50 rounded-lg"
                    />
                )}
                <div
                    tw="flex-1 grid grid-cols-3 gap-2 text-sm text-text-pale all-child:(overflow-hidden
                        whitespace-nowrap overflow-ellipsis)">
                    <div tw="text-text-dark font-semibold">{desc.title}</div>
                    <div>{desc.subtitle}</div>
                    <div>{desc.emphasis}</div>
                </div>
                <div tw="flex opacity[var(--button-opacity)] transition-opacity ml-4 items-center">
                    {primary !== undefined && (
                        <WireButton
                            data-testid="card-primary-button"
                            appearance={Appearance.Filled}
                            {...makeActionSpreadProps(primary)}
                        />
                    )}
                    {secondary !== undefined && (
                        <WireButton
                            tw="not-first:ml-2"
                            appearance={Appearance.Bordered}
                            {...makeActionSpreadProps(secondary)}
                        />
                    )}
                    {menuActions.length > 0 && (
                        <WireMenuButton tw="not-first:ml-2" appearance={Appearance.Bordered} menuItems={menuActions} />
                    )}
                </div>
            </div>
        </SectionStyleProvider>
    );
};

const Article: React.VFC<CardProps> = p => {
    const { desc } = p;
    const [primary, secondary, ...menuActions] = breakoutActions(desc.menuItems, 2);
    return (
        <SectionStyleProvider value={UIBackgroundStyle.Highlight}>
            <div
                onClick={e => {
                    desc.onCardClick?.();
                    e.stopPropagation();
                }}
                css={[desc.onCardClick && tw`hover:shadow-xl-dark`]}
                tw="relative bg-n0 p-6 text-text-dark flex-col flex shadow-md w-full rounded-lg overflow-hidden
                    transition-shadow
                    gp-md:flex-row">
                {desc.image && (
                    <div
                        tw="hidden flex-shrink-0 mr-6
                            gp-md:block">
                        <Img
                            src={massageImageUrl(desc.image, { thumbnail: false, width: 208, height: 208 }, undefined)}
                            tw="object-cover bg-n50 rounded-lg w-20 h-20
                                gp-lg:(w-32 h-32)
                                gp-xl:(w-44 h-44)
                                gp-2xl:(w-52 h-52)"
                        />
                    </div>
                )}
                <div tw="flex-grow flex flex-col">
                    <div tw="flex">
                        {desc.image && (
                            <div
                                tw="flex-shrink-0 mr-6
                                    gp-md:hidden">
                                <Img
                                    src={massageImageUrl(
                                        desc.image,
                                        { thumbnail: false, width: 208, height: 208 },
                                        undefined
                                    )}
                                    tw="w-16 h-16 object-cover bg-n50 rounded-lg"
                                />
                            </div>
                        )}
                        <div tw="flex flex-col flex-1">
                            {desc.emphasis && (
                                <div
                                    tw="font-size[11px] line-height[20px] uppercase text-accent font-bold
                                        gp-md:font-size[12px]">
                                    {desc.emphasis}
                                </div>
                            )}
                            {desc.title && (
                                <h3
                                    tw="text-base font-extrabold whitespace-pre-wrap
                                        gp-md:text-lg
                                        gp-lg:text-xl
                                        gp-xl:text-2xl">
                                    {desc.title}
                                </h3>
                            )}
                            {desc.subtitle && (
                                <div tw="text-sm text-text-pale whitespace-pre-wrap">{desc.subtitle}</div>
                            )}
                        </div>
                    </div>
                    {desc.body && <p tw="text-sm mt-1.5 text-text-xpale whitespace-pre-wrap">{desc.body}</p>}
                    <div tw="flex mt-3">
                        {primary !== undefined && (
                            <WireButton
                                data-testid="card-primary-button"
                                appearance={Appearance.Filled}
                                {...makeActionSpreadProps(primary)}
                            />
                        )}
                        {secondary !== undefined && (
                            <WireButton
                                tw="not-first:ml-2"
                                appearance={Appearance.Bordered}
                                {...makeActionSpreadProps(secondary)}
                            />
                        )}
                        {menuActions.length > 0 && (
                            <WireMenuButton
                                tw="not-first:ml-2"
                                appearance={Appearance.Bordered}
                                menuItems={menuActions}
                            />
                        )}
                    </div>
                </div>
            </div>
        </SectionStyleProvider>
    );
};

export function CardCollection<T>(p: Props<T>): React.ReactElement | null {
    const { describeItem, numPages, page, pageItems, onPageChange, cardStyle, size, aspect } = p;

    let content: React.ReactNode;
    if (cardStyle === CardStyle.Table) {
        content = (
            <div tw="relative shadow-md rounded-lg overflow-hidden self-stretch">
                <div tw="overflow-x-auto overflow-y-hidden">
                    <div tw="flex flex-col min-width[700px]">
                        {pageItems.map((item, index) => {
                            const desc = describeItem(item);
                            const key = `${index}-${page}`;
                            return <Table key={key} desc={desc} />;
                        })}
                    </div>
                </div>
            </div>
        );
    } else if (cardStyle === CardStyle.Cover) {
        content = (
            <CoverContainer size={size}>
                {pageItems.map((item, index) => {
                    const desc = describeItem(item);
                    const key = `${index}-${page}`;
                    return <Cover key={key} desc={desc} size={size} />;
                })}
            </CoverContainer>
        );
    } else if (cardStyle === CardStyle.Card) {
        content = (
            <CardContainer size={size} aspect={aspect}>
                {pageItems.map((item, index) => {
                    const desc = describeItem(item);
                    const key = `${index}-${page}`;
                    return <Card key={key} desc={desc} size={size} />;
                })}
            </CardContainer>
        );
    } else {
        const styles = css`
            ${tw`grid w-full grid-cols-1 gap-4
              gp-md:gap-5
              gp-lg:gap-6`}
        `;
        let Component = Article;
        if (cardStyle === CardStyle.Article) {
            Component = Article;
        } else if (cardStyle === CardStyle.List) {
            Component = List;
        } else {
            assertNever(cardStyle);
        }
        content = (
            <div css={styles} className={classNames(size, cardStyle)}>
                {pageItems.map((item, index) => {
                    const desc = describeItem(item);
                    const key = `${index}-${page}`;
                    return <Component key={key} desc={desc} />;
                })}
            </div>
        );
    }

    return (
        <div tw="flex flex-col items-center">
            {content}
            {numPages > 1 && <Pager numPages={numPages} page={page} onPageChange={onPageChange} />}
        </div>
    );
}

interface PagerProps {
    readonly page: number;
    readonly numPages: number;
    readonly onPageChange?: (page: number) => void;
    readonly className?: string;
}

export const Pager: GlideFC<PagerProps> = p => {
    const { numPages, page, onPageChange } = p;

    const theme = useWireAppTheme();
    const sectionStyle = useSectionStyle();
    const sizeClass = useResponsiveSizeClass();

    let numItems = 2;
    if (sizeClass === "md") {
        numItems = 3;
    } else if (sizeClass === "lg") {
        numItems = 4;
    } else if (sizeClass === "xl" || sizeClass === "2xl") {
        numItems = 5;
    }

    const selectedBackgroundColor =
        sectionStyle === UIBackgroundStyle.Accent
            ? "rgba(255, 255, 255, 0.8)"
            : chroma(theme.primaryAccentColor).alpha(0.1).css();

    return (
        <div className={p.className} tw="flex mt-4 self-stretch justify-center">
            <WireButton
                appearance={Appearance.Bordered}
                disabled={page === 0}
                tw="max-width[320px] rounded-lg px-2! mr-2 h-9
                    gp-sm:px-3
                    gp-lg:(mr-6 flex-grow)"
                onClick={() => onPageChange?.(Math.max(0, page - 1))}>
                <div
                    tw="hidden
                        gp-lg:block">
                    Previous
                </div>
                <div tw="gp-lg:hidden">
                    <GlideIcon iconSize={16} icon="st-caret" rotateDeg={90} kind="stroke" />
                </div>
            </WireButton>
            <div tw="flex shadow-md rounded-lg overflow-hidden all-child:(px-1 py-2 bg-n0 h-9)">
                {rangeForPage(page, numPages, numItems).map((x, ind) => (
                    <button
                        key={ind}
                        onClick={() => x !== null && onPageChange?.(x)}
                        style={{
                            backgroundColor: x === page ? selectedBackgroundColor : undefined,
                        }}
                        disabled={x === null}
                        tw="text-text-base last:(border-r-0) hover:underline focus-visible:underline rounded-none
                            border-0 border-r border-solid border-border-base disabled:(hover:no-underline cursor-auto)"
                        css={[x === page && tw`font-bold text-accent`]}>
                        <div
                            tw="w-5 text-center
                                gp-sm:w-7">
                            {x === null ? "…" : x + 1}
                        </div>
                    </button>
                ))}
            </div>
            <WireButton
                appearance={Appearance.Bordered}
                disabled={page === numPages - 1}
                onClick={() => onPageChange?.(Math.min(numPages - 1, page + 1))}
                tw="max-width[320px] rounded-lg px-2! ml-2 h-9
                    gp-sm:px-3
                    gp-lg:(ml-6 flex-grow)">
                <div
                    tw="hidden
                        gp-lg:block">
                    Next
                </div>
                <div tw="gp-lg:hidden">
                    <GlideIcon iconSize={16} icon="st-caret" rotateDeg={-90} kind="stroke" />
                </div>
            </WireButton>
        </div>
    );
};

export const CardCollectionRenderer: WireRenderer<WireListCardCollection> = React.memo(p => {
    const { backend } = p;
    const pageIndexToken = p.paging?.pageIndex.onChangeToken;

    return (
        <WireListContainer
            title={p.componentTitle ?? ""}
            titleActions={extractActions(p.titleActions, backend)}
            filterValue={definedMap(p.dynamicFilterCurrentIndex, i => p.dynamicFilterNames?.[i.value])}
            filterItems={p.dynamicFilterNames}
            onFilterChange={definedMap(p.dynamicFilterCurrentIndex?.onChangeToken, t => (newIndex: number) => {
                backend.valueChanged(t, newIndex);
            })}
            searchValue={p.searchBar?.value}
            onSearchChange={definedMap(p.searchBar?.onChangeToken, x => (newVal: string) => {
                backend.valueChanged(x, newVal);
                // Changing the search makes paging jump to the first page.
                if (pageIndexToken !== undefined) {
                    backend.valueChanged(pageIndexToken, 0);
                }
            })}
            appKind={backend.appKind}>
            <CardCollection
                pageItems={p.items}
                size={p.size ?? UISize.Small}
                numPages={p.paging?.numPages ?? 1}
                page={p.paging?.pageIndex.value ?? 0}
                aspect={p.aspectRatio ?? UIAspect.SixteenByNine}
                describeItem={x => {
                    const { action, itemActions, ...rest } = x;

                    return {
                        ...rest,
                        onCardClick: definedMap(action?.token, token => () => backend.runAction(token, false)),
                        menuItems: extractActions(itemActions, backend),
                    };
                }}
                cardStyle={p.cardStyle}
                onPageChange={definedMap(pageIndexToken, t => pageIndex => {
                    backend.valueChanged(t, pageIndex);
                })}
            />
        </WireListContainer>
    );
});
